import React from 'react';
import { Link, useParams } from 'react-router-dom';
import Navbar from '../../components/Navbar';
import {
  ChartBarIcon,
  DocumentTextIcon,
  ExclamationTriangleIcon,
  ChartPieIcon,
  ArrowTrendingUpIcon as TrendingUpIcon,
  LightBulbIcon,
  ArrowRightIcon,
  UserGroupIcon
} from '@heroicons/react/24/outline';

export default function DashboardGuidePage() {
  const { orgId } = useParams();
  const basePath = orgId ? `/${orgId}` : '';

  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar />
      
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Breadcrumb */}
        <nav className="mb-8">
          <ol className="flex items-center space-x-2 text-sm">
            <li>
              <Link to={`${basePath}/help`} className="text-gray-500 hover:text-gray-700">
                Help Center
              </Link>
            </li>
            <li className="text-gray-500">/</li>
            <li className="text-gray-900">Dashboard Guide</li>
          </ol>
        </nav>

        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 flex items-center">
            <ChartBarIcon className="h-8 w-8 text-blue-600 mr-3" />
            Master Your Flow Health Dashboard
          </h1>
          <p className="mt-2 text-lg text-gray-600">
            Learn how to use FlowDocs' comprehensive dashboard to monitor flow health, track improvements, and analyze real-time error data from your Salesforce org.
          </p>
        </div>

        {/* Overview Section */}
        <section className="mb-12">
          <h2 className="text-2xl font-semibold text-gray-900 mb-6">Understanding Your Dashboard</h2>
          
          <div className="bg-white rounded-lg shadow-sm p-6">
            <p className="text-gray-700 mb-4">
              The FlowDocs Dashboard provides a comprehensive view of your Salesforce flow health, helping you identify issues, track improvements, and monitor flow errors from actual user experiences.
            </p>
            
            <div className="mt-4 grid md:grid-cols-2 gap-4">
              <div className="p-4 bg-blue-50 rounded-md">
                <h3 className="font-medium text-blue-900 mb-2">Real-Time Insights:</h3>
                <ul className="space-y-1 text-sm text-blue-700">
                  <li>• Overall org health grade</li>
                  <li>• Critical issues requiring attention</li>
                  <li>• Flow error trends from user interviews</li>
                  <li>• Most affected users and processes</li>
                </ul>
              </div>
              
              <div className="p-4 bg-purple-50 rounded-md">
                <h3 className="font-medium text-purple-900 mb-2">Actionable Analytics:</h3>
                <ul className="space-y-1 text-sm text-purple-700">
                  <li>• Quick wins for easy improvements</li>
                  <li>• Issue distribution by type</li>
                  <li>• Historical trends and burn-down</li>
                  <li>• Error type breakdowns</li>
                </ul>
              </div>
            </div>
          </div>
        </section>

        {/* Key Features Section */}
        <section className="mb-12">
          <h2 className="text-2xl font-semibold text-gray-900 mb-6">Key Dashboard Features</h2>
          
          <div className="space-y-6">
            <div className="bg-white rounded-lg shadow-sm p-6">
              <div className="flex items-start">
                <ChartBarIcon className="h-6 w-6 text-blue-600 mt-1" />
                <div className="ml-4">
                  <h3 className="text-lg font-medium text-gray-900">Key Metrics Cards</h3>
                  <p className="mt-1 text-gray-600">
                    Four interactive metric cards provide instant insights and quick navigation to filtered views.
                  </p>
                  <ul className="mt-3 space-y-2 text-sm text-gray-600">
                    <li>• <strong>Health Grade:</strong> Overall org grade (A+ to I) with average score. Click to view all flows.</li>
                    <li>• <strong>Critical Issues:</strong> High-severity problems count. Click to see affected flows.</li>
                    <li>• <strong>Incomplete Flows:</strong> Flows graded "I" for missing analysis. Click to filter.</li>
                    <li>• <strong>Impacted Users:</strong> Users with flow error interviews. Click for user breakdown.</li>
                  </ul>
                  <div className="mt-3 p-3 bg-gray-50 rounded-md">
                    <p className="text-xs text-gray-600">💡 Each card is clickable and navigates to a pre-filtered view for quick action</p>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-lg shadow-sm p-6">
              <div className="flex items-start">
                <DocumentTextIcon className="h-6 w-6 text-green-600 mt-1" />
                <div className="ml-4">
                  <h3 className="text-lg font-medium text-gray-900">Quick Wins Widget</h3>
                  <p className="mt-1 text-gray-600">
                    Automatically identifies easy improvements grouped by issue type for efficient fixing.
                  </p>
                  <ul className="mt-3 space-y-2 text-sm text-gray-600">
                    <li>• <strong>Grouped by Type:</strong> Similar issues grouped together (e.g., "Missing Description")</li>
                    <li>• <strong>Time Estimates:</strong> Shows estimated minutes to fix each group</li>
                    <li>• <strong>Point Values:</strong> Potential score improvement for addressing the group</li>
                    <li>• <strong>Expandable Groups:</strong> Click to see all flows with the same issue</li>
                    <li>• <strong>Direct Flow Links:</strong> Navigate directly to any affected flow</li>
                  </ul>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-lg shadow-sm p-6">
              <div className="flex items-start">
                <ChartPieIcon className="h-6 w-6 text-purple-600 mt-1" />
                <div className="ml-4">
                  <h3 className="text-lg font-medium text-gray-900">Issue Distribution Chart</h3>
                  <p className="mt-1 text-gray-600">
                    Interactive horizontal bar chart showing issue types and affected flow counts.
                  </p>
                  <ul className="mt-3 space-y-2 text-sm text-gray-600">
                    <li>• <strong>Visual Progress Bars:</strong> Colored by severity (red/amber/blue)</li>
                    <li>• <strong>Issue Names:</strong> Clear descriptions like "Missing Description" or "Complex Flow"</li>
                    <li>• <strong>Affected Flows Count:</strong> Shows number of flows with each issue</li>
                    <li>• <strong>Click to Filter:</strong> Click any bar to see affected flows</li>
                    <li>• <strong>Top Issues First:</strong> Sorted by impact to guide priorities</li>
                  </ul>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-lg shadow-sm p-6">
              <div className="flex items-start">
                <TrendingUpIcon className="h-6 w-6 text-indigo-600 mt-1" />
                <div className="ml-4">
                  <h3 className="text-lg font-medium text-gray-900">Multiple Chart Views</h3>
                  <p className="mt-1 text-gray-600">
                    Various interactive charts provide different perspectives on your flow health.
                  </p>
                  <div className="mt-3 space-y-3">
                    <div>
                      <h4 className="font-medium text-gray-900">Burn-down Chart</h4>
                      <p className="text-sm text-gray-600">Stacked column chart showing issues fixed vs. new issues by date. Click columns to see flows.</p>
                    </div>
                    <div>
                      <h4 className="font-medium text-gray-900">Flow Error Trends</h4>
                      <p className="text-sm text-gray-600">Line chart tracking flow interview errors over time. Sync button updates from Salesforce.</p>
                    </div>
                    <div>
                      <h4 className="font-medium text-gray-900">Error Distribution</h4>
                      <p className="text-sm text-gray-600">Bar chart of error types from user interviews. Shows frequency and patterns.</p>
                    </div>
                    <div>
                      <h4 className="font-medium text-gray-900">Most Affected Users</h4>
                      <p className="text-sm text-gray-600">Expandable table showing users with most flow errors. Links to user details in Salesforce.</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Flow Error Analysis */}
        <section className="mb-12">
          <h2 className="text-2xl font-semibold text-gray-900 mb-6">Flow Interview Error Analysis</h2>
          
          <div className="space-y-6">
            <div className="bg-white rounded-lg shadow-sm p-6">
              <div className="flex items-start">
                <ExclamationTriangleIcon className="h-6 w-6 text-red-600 mt-1" />
                <div className="ml-4">
                  <h3 className="text-lg font-medium text-gray-900">Real-time Error Tracking from Salesforce</h3>
                  <p className="mt-1 text-gray-600">
                    Sync and analyze actual flow interview errors directly from your Salesforce org.
                  </p>
                  <ul className="mt-3 space-y-2 text-sm text-gray-600">
                    <li>• <strong>Sync Flow Interviews:</strong> Button to pull latest error data from Salesforce</li>
                    <li>• <strong>Error Trends Chart:</strong> Visual timeline of flow errors over past 30 days</li>
                    <li>• <strong>Error Type Distribution:</strong> Breakdown of error messages and frequencies</li>
                    <li>• <strong>User Impact Analysis:</strong> Identify users experiencing the most errors</li>
                    <li>• <strong>Flow-specific Errors:</strong> See which flows are failing most often</li>
                  </ul>
                  <div className="mt-4 p-3 bg-yellow-50 rounded-md border border-yellow-200">
                    <p className="text-sm text-yellow-800">
                      <strong>Note:</strong> Error data requires manual sync. Click "Sync Flow Interviews" to fetch latest data from Salesforce.
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-lg shadow-sm p-6">
              <div className="flex items-start">
                <UserGroupIcon className="h-6 w-6 text-orange-600 mt-1" />
                <div className="ml-4">
                  <h3 className="text-lg font-medium text-gray-900">Interactive User Impact Features</h3>
                  <p className="mt-1 text-gray-600">
                    Multiple ways to analyze and address user-facing flow errors.
                  </p>
                  <div className="mt-3 space-y-3">
                    <div>
                      <h4 className="font-medium text-gray-900">Affected Users Summary</h4>
                      <p className="text-sm text-gray-600">Shows total count with expandable details</p>
                    </div>
                    <div>
                      <h4 className="font-medium text-gray-900">Most Affected Users Table</h4>
                      <ul className="mt-1 space-y-1 text-sm text-gray-600">
                        <li>• User names with error counts</li>
                        <li>• Failed flow names and timestamps</li>
                        <li>• Direct Salesforce profile links</li>
                        <li>• Expandable rows for error details</li>
                      </ul>
                    </div>
                    <div>
                      <h4 className="font-medium text-gray-900">Flows by Error Type</h4>
                      <p className="text-sm text-gray-600">Groups flows by their error messages for pattern analysis</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Common Workflows */}
        <section className="mb-12">
          <h2 className="text-2xl font-semibold text-gray-900 mb-6">Common Dashboard Workflows</h2>
          
          <div className="space-y-4">
            <div className="bg-white rounded-lg shadow-sm p-6">
              <h3 className="font-medium text-gray-900 mb-3">📊 Initial Org Assessment</h3>
              <ol className="space-y-2 text-sm text-gray-600">
                <li>1. Check overall Health Grade (A+ to I scale)</li>
                <li>2. Review Critical Issues count - click card to see affected flows</li>
                <li>3. Expand Quick Wins groups to find easy improvements</li>
                <li>4. Review Issue Distribution to understand problem patterns</li>
              </ol>
            </div>

            <div className="bg-white rounded-lg shadow-sm p-6">
              <h3 className="font-medium text-gray-900 mb-3">🔄 Production Error Investigation</h3>
              <ol className="space-y-2 text-sm text-gray-600">
                <li>1. Click "Sync Flow Interviews" to get latest error data</li>
                <li>2. Review Flow Error Trends chart for patterns</li>
                <li>3. Click "Impacted Users" card to expand user details</li>
                <li>4. Use Error Type Distribution to identify common failures</li>
                <li>5. Navigate directly to problematic flows from any chart</li>
              </ol>
            </div>

            <div className="bg-white rounded-lg shadow-sm p-6">
              <h3 className="font-medium text-gray-900 mb-3">📈 Progress Tracking</h3>
              <ol className="space-y-2 text-sm text-gray-600">
                <li>1. Monitor Burn-down Chart for issues fixed vs. new issues</li>
                <li>2. Click chart columns to see flows changed on specific dates</li>
                <li>3. Track Health Grade changes over time</li>
                <li>4. Use Quick Wins points to estimate improvement potential</li>
              </ol>
            </div>
          </div>
        </section>

        {/* Best Practices */}
        <section className="mb-12">
          <h2 className="text-2xl font-semibold text-gray-900 mb-6">Dashboard Best Practices</h2>
          
          <div className="bg-gradient-to-br from-yellow-50 to-orange-50 rounded-lg p-6 border border-yellow-200">
            <div className="flex items-start">
              <LightBulbIcon className="h-6 w-6 text-yellow-600 mt-1" />
              <div className="ml-4">
                <h3 className="text-lg font-medium text-gray-900 mb-3">Maximize Your Dashboard Effectiveness</h3>
                <ol className="space-y-3 text-gray-700">
                  <li>
                    <strong>1. Regular Error Syncing:</strong>
                    <p className="text-sm mt-1">Click "Sync Flow Interviews" weekly to keep error data current</p>
                  </li>
                  <li>
                    <strong>2. Use Interactive Navigation:</strong>
                    <p className="text-sm mt-1">Every chart and card is clickable - use them to quickly filter flows</p>
                  </li>
                  <li>
                    <strong>3. Group Similar Fixes:</strong>
                    <p className="text-sm mt-1">Expand Quick Wins groups to fix multiple flows with same issue</p>
                  </li>
                  <li>
                    <strong>4. Monitor User Impact:</strong>
                    <p className="text-sm mt-1">Prioritize fixes for flows affecting the most users</p>
                  </li>
                  <li>
                    <strong>5. Track Burn-down Progress:</strong>
                    <p className="text-sm mt-1">Use the stacked column chart to ensure you're fixing more than creating</p>
                  </li>
                </ol>
              </div>
            </div>
          </div>
        </section>

        {/* Next Steps */}
        <section className="mb-12">
          <h2 className="text-2xl font-semibold text-gray-900 mb-6">Next Steps</h2>
          
          <div className="grid gap-4">
            <Link
              to={`${basePath}/dashboard`}
              className="block p-4 bg-white rounded-lg shadow-sm hover:shadow-md transition-shadow border border-gray-200"
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center">
                  <ChartBarIcon className="h-5 w-5 text-gray-600 mr-3" />
                  <div>
                    <h4 className="font-medium text-gray-900">View Your Dashboard</h4>
                    <p className="text-sm text-gray-600">Start analyzing your flow health</p>
                  </div>
                </div>
                <ArrowRightIcon className="h-5 w-5 text-gray-400" />
              </div>
            </Link>

            <Link
              to={`${basePath}/help/flows-guide`}
              className="block p-4 bg-white rounded-lg shadow-sm hover:shadow-md transition-shadow border border-gray-200"
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center">
                  <DocumentTextIcon className="h-5 w-5 text-gray-600 mr-3" />
                  <div>
                    <h4 className="font-medium text-gray-900">Flows Guide</h4>
                    <p className="text-sm text-gray-600">Learn about flow management features</p>
                  </div>
                </div>
                <ArrowRightIcon className="h-5 w-5 text-gray-400" />
              </div>
            </Link>
          </div>
        </section>

        {/* Footer */}
        <div className="mt-12 pt-8 border-t border-gray-200">
          <div className="flex justify-between items-center">
            <Link to={`${basePath}/help`} className="text-blue-600 hover:text-blue-800 flex items-center">
              <ArrowRightIcon className="h-4 w-4 mr-1 rotate-180" />
              Back to Help Center
            </Link>
            <Link to={`${basePath}/dashboard`} className="text-blue-600 hover:text-blue-800 flex items-center">
              View Dashboard
              <ArrowRightIcon className="h-4 w-4 ml-1" />
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}